
import { useCallback, useEffect, useState } from 'react';

interface Paginated<T> {
  data: T[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  totals: { pageAmount: number; globalAmount: number };
}

interface SaleRow {
  id: string;
  saleAt: string | null;
  amount: number;
  codigoComercio: string | null;
  cardType: string | null;
  cardBrand: string | null;
  cardNumber: string | null;
  authorizationCode: string | null;
  installmentType: string | null;
  numberOfInstallments: number | null;
}

interface InstallmentRow {
  id: string;
  installmentNumber: number;
  installmentAmount: number;
  dueAt: string;
  paymentStatus: string;
  paymentAt: string | null;
  paymentAmount: number | null;
  salesTransactionId: string;
  codigoComercio: string | null;
  authorizationCode: string | null;
  advanceRequestId: string | null;
  collectionStatus: string | null;
  mechanism: string | null;
}

type Tab = 'ventas' | 'cuotas';
type ApiFetcher = <T>(url: string) => Promise<T>;
type UrlBuilder = (resource: 'sales' | 'installments') => string;

const formatCLP = (n: number) => n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const COLLECTION_STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pendiente', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  sent: { label: 'Enviada', color: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  collected: { label: 'Cobrada', color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  uncollectible: { label: 'Incobrable', color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

interface Props {
  title?: string;
  description?: string;
  acquirerId: string;
  buildUrl: UrlBuilder;
  fetcher: ApiFetcher;
  /** Prefijo para links a adelantos (merchant `/adelantos/detalle?id=` vs admin `/admin/solicitudes/detalle?id=`) */
  advanceLinkPrefix: string;
}

export default function AcquirerDataPanel({ title = 'Movimientos y cuotas', description, buildUrl, fetcher, advanceLinkPrefix }: Props) {
  const [tab, setTab] = useState<Tab>('ventas');
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState<Paginated<SaleRow> | null>(null);
  const [installments, setInstallments] = useState<Paginated<InstallmentRow> | null>(null);
  const [page, setPage] = useState(1);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [codigoComercio, setCodigoComercio] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [collectionStatus, setCollectionStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', '20');
      if (codigoComercio) qs.set('codigoComercio', codigoComercio);
      if (tab === 'ventas') {
        if (fechaDesde) qs.set('fechaDesde', fechaDesde);
        if (fechaHasta) qs.set('fechaHasta', fechaHasta);
        const res = await fetcher<Paginated<SaleRow>>(`${buildUrl('sales')}?${qs.toString()}`);
        setSales(res);
      } else {
        if (fechaDesde) qs.set('dueDesde', fechaDesde);
        if (fechaHasta) qs.set('dueHasta', fechaHasta);
        if (paymentStatus) qs.set('paymentStatus', paymentStatus);
        if (collectionStatus) qs.set('collectionStatus', collectionStatus);
        const res = await fetcher<Paginated<InstallmentRow>>(`${buildUrl('installments')}?${qs.toString()}`);
        setInstallments(res);
      }
    } catch {
      if (tab === 'ventas') setSales(null);
      else setInstallments(null);
    } finally {
      setLoading(false);
    }
  }, [tab, page, fechaDesde, fechaHasta, codigoComercio, paymentStatus, collectionStatus, buildUrl, fetcher]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetPageAndReload = () => setPage(1);

  return (
    <section className="rounded-xl border bg-card p-6">
      <h2 className="text-base font-semibold">{title}</h2>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}

      <div className="mt-4 flex items-center gap-1 rounded-lg border border-input p-1 w-fit">
        <button
          type="button"
          onClick={() => { setTab('ventas'); setPage(1); }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'ventas' ? 'bg-flux-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
        >
          Ventas / Abonos
        </button>
        <button
          type="button"
          onClick={() => { setTab('cuotas'); setPage(1); }}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === 'cuotas' ? 'bg-flux-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
        >
          Cuotas
        </button>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <input
          type="date"
          value={fechaDesde}
          max={fechaHasta || undefined}
          onChange={(e) => { setFechaDesde(e.target.value); resetPageAndReload(); }}
          placeholder="Desde"
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={fechaHasta}
          min={fechaDesde || undefined}
          onChange={(e) => { setFechaHasta(e.target.value); resetPageAndReload(); }}
          placeholder="Hasta"
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        {fechaDesde && fechaHasta && fechaDesde > fechaHasta && (
          <p className="md:col-span-4 text-xs text-red-600 dark:text-red-400">La fecha desde no puede ser posterior a la fecha hasta.</p>
        )}
        <input type="text" value={codigoComercio} onChange={(e) => { setCodigoComercio(e.target.value); resetPageAndReload(); }} placeholder="Código comercio" className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
        {tab === 'cuotas' ? (
          <select value={collectionStatus} onChange={(e) => { setCollectionStatus(e.target.value); resetPageAndReload(); }} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="">Cobranza: todas</option>
            <option value="pending">Pendiente</option>
            <option value="sent">Enviada</option>
            <option value="collected">Cobrada</option>
            <option value="uncollectible">Incobrable</option>
          </select>
        ) : (
          <div />
        )}
      </div>

      <div className="mt-4 overflow-x-auto">
        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : tab === 'ventas' ? (
          <SalesTable response={sales} page={page} setPage={setPage} />
        ) : (
          <InstallmentsTable response={installments} page={page} setPage={setPage} advanceLinkPrefix={advanceLinkPrefix} />
        )}
      </div>
    </section>
  );
}

function SalesTable({ response, page, setPage }: { response: Paginated<SaleRow> | null; page: number; setPage: (p: number) => void }) {
  if (!response || response.data.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">Sin ventas con estos filtros.</p>;
  return (
    <>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-2 pr-3">Fecha</th>
            <th className="py-2 pr-3">Código</th>
            <th className="py-2 pr-3 text-right">Monto</th>
            <th className="py-2 pr-3">Tarjeta</th>
            <th className="py-2 pr-3">Tipo</th>
            <th className="py-2 pr-3">Autorización</th>
            <th className="py-2 pr-3 text-right">N° cuotas</th>
          </tr>
        </thead>
        <tbody>
          {response.data.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="py-2 pr-3 text-muted-foreground">{formatDate(r.saleAt)}</td>
              <td className="py-2 pr-3 font-mono text-xs">{r.codigoComercio ?? '—'}</td>
              <td className="py-2 pr-3 text-right font-medium">{formatCLP(r.amount)}</td>
              <td className="py-2 pr-3 text-xs">{r.cardBrand ?? r.cardType ?? '—'} {r.cardNumber ? `···${r.cardNumber.slice(-4)}` : ''}</td>
              <td className="py-2 pr-3 text-xs">{r.installmentType ?? '—'}</td>
              <td className="py-2 pr-3 font-mono text-xs">{r.authorizationCode ?? '—'}</td>
              <td className="py-2 pr-3 text-right">{r.numberOfInstallments ?? 1}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination response={response} page={page} setPage={setPage} />
    </>
  );
}

function InstallmentsTable({ response, page, setPage, advanceLinkPrefix }: { response: Paginated<InstallmentRow> | null; page: number; setPage: (p: number) => void; advanceLinkPrefix: string }) {
  if (!response || response.data.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">Sin cuotas con estos filtros.</p>;
  return (
    <>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-2 pr-3">Fecha objetivo</th>
            <th className="py-2 pr-3">Código</th>
            <th className="py-2 pr-3 text-right">Monto</th>
            <th className="py-2 pr-3">Estado pago</th>
            <th className="py-2 pr-3">Estado cobro</th>
            <th className="py-2 pr-3">Mecanismo</th>
            <th className="py-2 pr-3">Adelanto</th>
          </tr>
        </thead>
        <tbody>
          {response.data.map((r) => {
            const cs = r.collectionStatus ? COLLECTION_STATUS_LABEL[r.collectionStatus] : null;
            return (
              <tr key={r.id} className="border-t border-border">
                <td className="py-2 pr-3 text-muted-foreground">{r.dueAt}</td>
                <td className="py-2 pr-3 font-mono text-xs">{r.codigoComercio ?? '—'}</td>
                <td className="py-2 pr-3 text-right font-medium">{r.paymentAmount !== null ? formatCLP(r.paymentAmount) : '—'}</td>
                <td className="py-2 pr-3 text-xs">
                  <span className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-600 dark:text-slate-400">{r.paymentStatus}</span>
                </td>
                <td className="py-2 pr-3 text-xs">
                  {cs ? <span className={`inline-flex rounded-full px-2 py-0.5 ${cs.color}`}>{cs.label}</span> : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 pr-3 text-xs">{r.mechanism ?? '—'}</td>
                <td className="py-2 pr-3 text-xs font-mono">
                  {r.advanceRequestId ? (
                    <a href={`${advanceLinkPrefix}${r.advanceRequestId}`} className="text-flux-primary hover:underline">{r.advanceRequestId.slice(0, 10)}…</a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination response={response} page={page} setPage={setPage} />
    </>
  );
}

function Pagination<T>({ response, page, setPage }: { response: Paginated<T>; page: number; setPage: (p: number) => void }) {
  if (response.pagination.totalPages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm">
      <p className="text-muted-foreground">
        Página {response.pagination.page} de {response.pagination.totalPages} · Total {formatCLP(response.totals.globalAmount)}
      </p>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))} className="rounded-md border px-3 py-1 text-sm disabled:opacity-40">Anterior</button>
        <button disabled={page >= response.pagination.totalPages} onClick={() => setPage(page + 1)} className="rounded-md border px-3 py-1 text-sm disabled:opacity-40">Siguiente</button>
      </div>
    </div>
  );
}
